import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  compose,
} from '../../hooks/useService';
import { ServiceAuth } from '../../hooks/useService/useService';
import urls from '../../util/urls';

export type GetSurveysResponse = {
  items: fhir.Questionnaire[];
};

export default class SurveyFilesService {
  private getSurveyAttachmentsAPI: (auth: ServiceAuth) => AxiosInstance;
  private getUploadAPI: () => AxiosInstance;

  constructor(options?: Partial<AxiosRequestConfig>) {
    this.getSurveyAttachmentsAPI = (auth: ServiceAuth) =>
      compose(
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create(options));

    this.getUploadAPI = () => withRetries()(axios.create(options));
  }

  getDownloadLink =
    (auth: ServiceAuth) =>
    async (
      projectId: string,
      responseId: string,
      linkId: string,
      fileId: string,
      includeContentDisposition = true,
      cohort?: string,
    ) => {
      const params = {
        include: 'downloadUrl',
        includeContentDisposition,
        ...(cohort ? { cohort } : {}),
      };

      return this.getSurveyAttachmentsAPI(auth).get(
        urls.api.survey.surveyAttachment(
          projectId,
          responseId,
          linkId,
          fileId,
          params,
        ),
      );
    };

  createSurveyFile =
    (auth: ServiceAuth) =>
    async ({
      projectId,
      responseId,
      linkId,
      file,
      cohort,
    }: {
      projectId: string;
      responseId: string;
      linkId: string;
      file: File;
      cohort: string;
    }) =>
      this.getSurveyAttachmentsAPI(auth).put(
        urls.api.survey.surveyAttachment(
          projectId,
          responseId,
          linkId,
          undefined,
          { cohort },
        ),
        {
          fileName: file.name,
          contentType: file.type,
        },
      );

  uploadSurveyFile =
    () => async (uploadUrl: string, contentType: string, file: File) =>
      this.getUploadAPI().put(uploadUrl, file, {
        headers: {
          'Content-Type': contentType,
        },
      });
}
